import { Component, Suspense, type ReactNode } from 'react';
import { RotateCw } from 'lucide-react';
import { Outlet, useLocation } from 'react-router';
import { Notice } from '@/components/Notice';
import { Button } from '@/components/ui/button';
import { reloadPage } from './lazy-page';

/**
 * Where a private screen renders, inside the frame's `<main>`. Both ways a screen can be missing are handled
 * here, below the frame, so the sidebar, the band, the palette and the inbox never unmount for either:
 *
 *   - its chunk is still on the way: the same loading line every screen shows before its first read lands
 *     (`.page-loading` — the hook the responsive suite waits on), plain text in the flow of the page as
 *     theirs is. It holds no heading: the screen's own title is the page's one `h1` when it arrives.
 *   - its chunk cannot be had: a tab left open across a deploy asks for a hash the server no longer has.
 *     Without a boundary React would unmount the whole root and leave a blank page.
 */
export function RouteOutlet() {
  const { pathname } = useLocation();
  return (
    <RouteErrorBoundary resetKey={pathname}>
      <Suspense fallback={<RouteLoading />}>
        <Outlet />
      </Suspense>
    </RouteErrorBoundary>
  );
}

export function RouteLoading() {
  return <p className="page-loading" data-slot="route-loading">Đang tải…</p>;
}

type BoundaryProps = { resetKey: string; children: ReactNode };
type BoundaryState = { failed: boolean; resetKey: string };

/**
 * Catches a screen that failed to load or to render. It clears itself when the path changes, so following a
 * link out of a broken screen reaches the next one; the broken screen itself stays broken until a reload,
 * because `React.lazy` keeps the rejection it got. React reports the error to the console on its own.
 */
export class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(): Partial<BoundaryState> {
    return { failed: true };
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): Partial<BoundaryState> | null {
    return props.resetKey === state.resetKey ? null : { failed: false, resetKey: props.resetKey };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex flex-col items-start gap-3">
        <Notice tone="error" role="alert">
          <p className="font-semibold">Không tải được trang này.</p>
          <p>Ứng dụng có thể vừa được cập nhật, hoặc kết nối vừa bị gián đoạn. Tải lại để mở lại trang.</p>
        </Notice>
        <Button type="button" variant="outline" onClick={() => reloadPage()}>
          <RotateCw aria-hidden="true" />
          Tải lại
        </Button>
      </div>
    );
  }
}
